import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import adminApi from "../../../services/adminApi";
import ReportShell from "../ReportShell";
import {
  DateRange,
  DEFAULT_RANGE,
  RANGE_PRESETS,
  REFUND_METHOD_LABELS,
  rangeLabel,
  rangeParams,
} from "../reportCommon";
import {
  ExportColumn,
  exportCsv,
  exportPdf,
  formatCurrency,
  pdfCurrency,
  PharmacyHeader,
} from "../../../utils/reportExport";

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface Props {
  pharmacy: PharmacyHeader;
}

const RefundReport: React.FC<Props> = ({ pharmacy }) => {
  const [range, setRange] = useState<DateRange>(DEFAULT_RANGE);
  const [refundMethod, setRefundMethod] = useState<string | undefined>();
  const [createdBy, setCreatedBy] = useState<string | undefined>();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(
    () => ({
      ...rangeParams(range),
      refundMethod,
      createdBy,
      search: search || undefined,
    }),
    [range, refundMethod, createdBy, search]
  );

  const load = useCallback(
    async (p = page, ps = pageSize) => {
      setLoading(true);
      setError(null);
      try {
        const res = await adminApi.getRefundReport({
          ...buildParams(),
          page: p,
          limit: ps,
        });
        setData(res.data.data);
      } catch (e: any) {
        setError(e.response?.data?.message || "Failed to load refund report");
      } finally {
        setLoading(false);
      }
    },
    [buildParams, page, pageSize]
  );

  useEffect(() => {
    load(1, pageSize);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, refundMethod, createdBy]);

  const summary = data?.summary || {};

  const exportColumns: ExportColumn[] = [
    { title: "Return #", value: (r) => r.returnNumber || "" },
    {
      title: "Date",
      value: (r) => (r.returnDate ? dayjs(r.returnDate).format("DD/MM/YYYY") : ""),
    },
    { title: "Bill #", value: (r) => r.bill?.billNumber || "" },
    {
      title: "Customer",
      value: (r) => r.patient?.name || r.bill?.customer?.name || "Walk-in",
    },
    {
      title: "Items",
      value: (r) =>
        (r.items || [])
          .map((i: any) => `${i.name || "Item"} x${i.quantity}`)
          .join("; "),
    },
    {
      title: "Method",
      value: (r) => REFUND_METHOD_LABELS[r.refundMethod] || r.refundMethod || "",
    },
    { title: "Items Value", value: (r) => (r.grandTotal || 0).toFixed(2) },
    { title: "Refund", value: (r) => (r.refundAmount || 0).toFixed(2) },
    { title: "Deduction", value: (r) => (r.deduction || 0).toFixed(2) },
    {
      title: "Net Refund",
      value: (r) => (r.netRefundAmount ?? r.grandTotal ?? 0).toFixed(2),
    },
    { title: "By", value: (r) => r.createdBy?.name || "" },
    { title: "Reason", value: (r) => r.reason || "" },
  ];

  const fetchAll = async () => {
    const res = await adminApi.getRefundReport({
      ...buildParams(),
      format: "all",
    });
    return res.data.data;
  };

  const handleExportCsv = async () => {
    const full = await fetchAll();
    const s = full.summary || {};
    exportCsv("Refund Report", exportColumns, full.returns || [], [
      `Period: ${rangeLabel(range)}`,
      `Returns: ${s.returnCount || 0} | Net refund: ${(s.netRefund || 0).toFixed(2)}`,
    ]);
  };

  const handleExportPdf = async () => {
    const full = await fetchAll();
    const s = full.summary || {};
    exportPdf({
      title: "Refund Report",
      pharmacy,
      subtitle: `Period: ${rangeLabel(range)}`,
      orientation: "landscape",
      columns: exportColumns,
      rows: full.returns || [],
      summary: [
        { label: "Returns", value: String(s.returnCount || 0) },
        { label: "Items value", value: pdfCurrency(s.itemsValue) },
        { label: "Refund", value: pdfCurrency(s.refundAmount) },
        { label: "Deduction", value: pdfCurrency(s.deduction) },
        { label: "Net refund", value: pdfCurrency(s.netRefund) },
      ],
    });
  };

  const reset = () => {
    setRange(DEFAULT_RANGE);
    setRefundMethod(undefined);
    setCreatedBy(undefined);
    setSearch("");
  };

  return (
    <ReportShell
      title="Refunds / Sale Returns"
      loading={loading}
      error={error}
      onRefresh={() => load()}
      onReset={reset}
      onExportCsv={handleExportCsv}
      onExportPdf={handleExportPdf}
      filters={
        <>
          <RangePicker
            size="small"
            value={range}
            allowClear={false}
            presets={RANGE_PRESETS}
            onChange={(v) => v && setRange(v as DateRange)}
          />
          <Select
            size="small"
            allowClear
            placeholder="Refund method"
            style={{ width: 140 }}
            value={refundMethod}
            onChange={setRefundMethod}
            options={Object.entries(REFUND_METHOD_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
          <Select
            size="small"
            allowClear
            placeholder="Returned by"
            style={{ width: 140 }}
            value={createdBy}
            onChange={setCreatedBy}
            options={(data?.byUser || []).map((u: any) => ({
              value: u._id,
              label: u.name,
            }))}
          />
          <Input.Search
            size="small"
            placeholder="Bill # / return #"
            style={{ width: 180 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={() => {
              setPage(1);
              load(1, pageSize);
            }}
            allowClear
          />
        </>
      }
      stats={[
        { title: "Returns", value: summary.returnCount || 0 },
        { title: "Qty returned", value: summary.qtyReturned || 0 },
        {
          title: "Items value",
          value: summary.itemsValue || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Refund amount",
          value: summary.refundAmount || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Deduction",
          value: summary.deduction || 0,
          prefix: "₹",
          precision: 2,
        },
        {
          title: "Net refund",
          value: summary.netRefund || 0,
          prefix: "₹",
          precision: 2,
        },
      ]}
      extra={
        (data?.byMethod || []).length > 0 ? (
          <Card size="small" style={{ marginBottom: 12 }} styles={{ body: { padding: "8px 12px" } }}>
            <Space wrap split="·">
              {(data.byMethod || []).map((m: any) => (
                <Text key={m._id || "na"} style={{ fontSize: 12 }}>
                  {REFUND_METHOD_LABELS[m._id] || m._id || "—"}:{" "}
                  <strong>{formatCurrency(m.netRefund)}</strong> ({m.count})
                </Text>
              ))}
            </Space>
          </Card>
        ) : null
      }
    >
      <Table
        rowKey="_id"
        size="small"
        sticky
        loading={loading}
        dataSource={data?.returns || []}
        scroll={{ x: 1000 }}
        pagination={{
          current: page,
          pageSize,
          total: data?.pagination?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `${t} returns`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            load(p, ps);
          },
        }}
        expandable={{
          expandedRowRender: (r: any) => (
            <Table
              size="small"
              pagination={false}
              rowKey={(_: any, i?: number) => String(i)}
              dataSource={r.items || []}
              columns={[
                { title: "Medicine", render: (_: unknown, item: any) => item.name || "—" },
                { title: "Batch", dataIndex: "batchNumber", width: 100 },
                { title: "Qty", dataIndex: "quantity", width: 70, align: "right" as const },
                {
                  title: "Rate",
                  dataIndex: "unitPrice",
                  width: 100,
                  align: "right" as const,
                  render: (v: number) => formatCurrency(v),
                },
                {
                  title: "Total",
                  dataIndex: "total",
                  width: 100,
                  align: "right" as const,
                  render: (v: number) => formatCurrency(v),
                },
              ]}
            />
          ),
        }}
        columns={[
          { title: "Return #", dataIndex: "returnNumber", width: 120 },
          {
            title: "Date",
            dataIndex: "returnDate",
            width: 100,
            render: (d: string) => (d ? dayjs(d).format("DD/MM/YYYY") : "—"),
          },
          {
            title: "Bill #",
            width: 110,
            render: (_: unknown, r: any) => r.bill?.billNumber || "—",
          },
          {
            title: "Customer",
            ellipsis: true,
            render: (_: unknown, r: any) =>
              r.patient?.name || r.bill?.customer?.name || "Walk-in",
          },
          {
            title: "Method",
            dataIndex: "refundMethod",
            width: 110,
            render: (m: string) => REFUND_METHOD_LABELS[m] || m || "—",
          },
          {
            title: "Refund",
            dataIndex: "refundAmount",
            width: 100,
            align: "right" as const,
            render: (v: number) => formatCurrency(v),
          },
          {
            title: "Deduction",
            dataIndex: "deduction",
            width: 100,
            align: "right" as const,
            render: (v: number) => formatCurrency(v),
          },
          {
            title: "Net",
            width: 100,
            align: "right" as const,
            render: (_: unknown, r: any) =>
              formatCurrency(r.netRefundAmount ?? r.grandTotal),
          },
          {
            title: "By",
            width: 100,
            ellipsis: true,
            render: (_: unknown, r: any) => r.createdBy?.name || "—",
          },
          { title: "Reason", dataIndex: "reason", ellipsis: true },
        ]}
      />
    </ReportShell>
  );
};

export default RefundReport;
